import { designVariables as tk } from "@studio3t/design-variables";
import {
  GenericDropdown,
  GenericDropdownContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import type React from "react";
import { useCallback, useMemo, useState } from "react";
import { Group, Panel, useDefaultLayout } from "react-resizable-panels";
import styled from "styled-components";
import { AGENT_SYSTEM_PROMPT } from "@/api/ai/agentSystemPrompt";
import type { ConversationMode } from "@/api/ai/types";
import { useAnyModalOpen } from "@/components/ModalChrome";
import { ColumnSeparator } from "@/components/PanelSeparators";
import { SkeletonBar } from "@/components/Skeleton";
import { useEntitlementsPolling } from "@/hooks";
import { useLlmServices } from "@/hooks/useLlmServices";
import { usePersistedState } from "@/hooks/usePersistedState";
import { ActionsTrigger } from "@/pages/PipelineJobActions";
import { parseOneOf } from "@/utils/storedValue";
import { userStorage } from "@/utils/userStorage";
import { AiChatProvider, useAiChatContext } from "./AiChatContext";
import { ChatDeleteModal } from "./ChatDeleteModal";
import { ChatInput } from "./ChatInput";
import { ChatNameModal } from "./ChatNameModal";
import { ChatWindow, PAGE_CONTENT_WIDTH } from "./ChatWindow";
import { ModelSelector } from "./ModelSelector";
import { ModeSelector } from "./ModeSelector";
import { sessionList } from "./sessionList";

const LIST_ITEM = tk.ui.list.item;
const SINGLE_LINE = tk.type.singleLine;

// A sidebar row (Figma List/item): the leading icon in its wrap, the
// label, and an optional trailing control shown while the row is hovered
// or focused. The icon takes the list's active tint on the active row
// and under the pointer.
function SessionRow({
  icon,
  iconColorKey,
  label,
  active,
  onActivate,
  trailing,
  testId,
}: {
  icon: Icon;
  /** A fixed tint instead of the list's state-driven one. */
  iconColorKey?: SVGColorKey;
  label: string;
  active: boolean;
  onActivate: () => void;
  trailing?: React.ReactNode;
  testId?: string;
}) {
  const [hovered, setHovered] = useState(false);
  const stateColorKey =
    active || hovered
      ? SVGColorKey.listItemIconActive
      : SVGColorKey.listItemIcon;
  // The row is a plain container: the session is a button of its own,
  // beside the menu's button, so neither control sits inside the other
  // (nested controls make an invalid accessibility tree).
  return (
    <Row
      $active={active}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <RowButton
        type="button"
        aria-current={active || undefined}
        onClick={onActivate}
        data-testid={testId}
      >
        <RowIcon>
          <IconSVG
            icon={icon}
            size={LIST_ITEM.icon}
            svgColorKey={iconColorKey ?? stateColorKey}
          />
        </RowIcon>
        <RowLabel>{label}</RowLabel>
      </RowButton>
      {trailing}
    </Row>
  );
}

// The row's "···" button (the Pipelines table's actions trigger, with its
// grey hover backdrop) and its menu. Clicks and keys inside stay
// out of the row, so opening the menu does not switch sessions.
function SessionMenu({
  onRename,
  onDelete,
}: {
  onRename: () => void;
  onDelete: () => void;
}) {
  const items = useMemo<ListItemProps[][]>(
    () => [
      [
        {
          id: "rename",
          label: "Rename",
          type: "text",
          value: "rename",
          leadingIcon: Icon.Edit,
          onClick: onRename,
        },
        {
          id: "delete",
          label: "Delete",
          type: "text",
          value: "delete",
          leadingIcon: Icon.Bin,
          leadingIconColorKey: SVGColorKey.errorIcon,
          onClick: onDelete,
        },
      ],
    ],
    [onRename, onDelete],
  );
  return (
    <MenuSlot>
      <GenericDropdown
        dropdownItems={items}
        width={{ strategy: "fixed", size: 160 }}
        placement="bottom-end"
      >
        <GenericDropdownContext.Consumer>
          {({ open }) => (
            <RowTrailing
              $open={open}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <ActionsTrigger
                type="button"
                title="Session options"
                aria-label="Session options"
              >
                <IconSVG
                  icon={Icon.MoreH}
                  size={tk.ui.button.iconsize}
                  svgColorKey={SVGColorKey.semanticTextBody}
                />
              </ActionsTrigger>
            </RowTrailing>
          )}
        </GenericDropdownContext.Consumer>
      </GenericDropdown>
    </MenuSlot>
  );
}

function SessionSidebar({
  conversations,
  currentId,
  onSelect,
  onNew,
  newActive,
  onRename,
  onDelete,
  loading,
}: {
  conversations: { id: string; title: string }[];
  currentId?: string;
  onSelect: (id: string) => void;
  onNew: () => void;
  /** The current session is the empty one the New row stands for. */
  newActive: boolean;
  onRename: (id: string) => void;
  onDelete: (id: string) => void;
  /** The list is still on its way: skeleton rows stand in. */
  loading: boolean;
}) {
  return (
    <Sidebar>
      <SidebarHeader>
        <SidebarTitle>Sessions</SidebarTitle>
      </SidebarHeader>
      <SessionList>
        <SessionRow
          icon={Icon.Plus}
          iconColorKey={SVGColorKey.semanticTextBody}
          label="New"
          active={newActive}
          onActivate={() => {
            if (!newActive) onNew();
          }}
          testId="agent-new-session"
        />
        {loading && <SessionSkeletonRows />}
        {conversations.map((conv) => (
          <SessionRow
            key={conv.id}
            icon={Icon.SelectorDot}
            label={conv.title}
            active={conv.id === currentId}
            onActivate={() => onSelect(conv.id)}
            trailing={
              <SessionMenu
                onRename={() => onRename(conv.id)}
                onDelete={() => onDelete(conv.id)}
              />
            }
          />
        ))}
      </SessionList>
    </Sidebar>
  );
}

// Session-shaped placeholders while the list loads: the dot and a title
// bar of varying length, on the row's own box.
const SKELETON_ROWS = ["62%", "78%", "54%", "70%"];

function SessionSkeletonRows() {
  return (
    <>
      {SKELETON_ROWS.map((width) => (
        <SkeletonRow key={width} aria-hidden="true">
          <SkeletonBar $width={`${LIST_ITEM.icon}px`} $height="6px" />
          <SkeletonBar $width={width} />
        </SkeletonRow>
      ))}
    </>
  );
}

/// Owns the conversation-mode state plus the entitlement-driven Act gate.
/// Trim + lowercase before comparing so `"FALSE"` / `" false "` / `"False"`
/// all disable Act in the UI, matching the Rust accessor's tolerance.
// Per-user Agent page preferences (TTTL-186); the defaults are stored as
// absence.
const STORAGE_KEYS = {
  mode: "agent:mode",
} as const;
const MODES: ConversationMode[] = ["ask", "act"];
// The session sidebar's width band. Pixels, not a share of the page: the
// sidebar keeps its width when the window resizes, and double-clicking the
// separator returns it to `initial` (the package resets to defaultSize).
// Figma Bridge_Sidebar (15846-137686) is 330 wide including the card's
// left border and the divider, so the panel between them is 328.
const SIDEBAR = { initial: "328px", min: "180px", max: "480px" } as const;
const LAYOUT_ID = "agent-page";

function useActModeGate() {
  const { data: entitlements } = useEntitlementsPolling();
  const actDisabled =
    entitlements?.features?.agent_enabled?.trim().toLowerCase() === "false";

  // The user's pick is remembered; while the entitlement gate is on the
  // page runs in Ask regardless (e.g. after a cert refresh), and the pick
  // comes back once the gate lifts.
  const [chosenMode, setMode] = usePersistedState<ConversationMode>(
    STORAGE_KEYS.mode,
    (raw) => parseOneOf(raw, MODES) ?? "act",
    serializeMode,
  );
  const mode: ConversationMode = actDisabled ? "ask" : chosenMode;

  return { mode, setMode, actDisabled };
}

function serializeMode(mode: ConversationMode): string | null {
  return mode === "act" ? null : mode;
}

interface AgentFooterProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  onSubmit: () => void;
  onAbort: () => void;
  isLoading: boolean;
  mode: ConversationMode;
  onModeChange: (mode: ConversationMode) => void;
  actDisabled: boolean;
}

// The Figma Bridge_AgentFooter: a card rising from the bottom edge that
// holds the prompt and, under it, the mode picker and the model picker.
// Picking a model activates that LLM service, so it is the same default
// the Services page sets.
function AgentFooter({
  inputValue,
  onInputChange,
  onSubmit,
  onAbort,
  isLoading,
  mode,
  onModeChange,
  actDisabled,
}: AgentFooterProps) {
  const { llmServices, activeLlm, activateLlm, isActivating } =
    useLlmServices();

  return (
    <FooterCard data-testid="agent-footer">
      <ChatInput
        value={inputValue}
        onChange={onInputChange}
        onSubmit={onSubmit}
        onAbort={onAbort}
        isLoading={isLoading}
        placeholder="Ask the agent to perform an action..."
        bare
      />
      <FooterControls>
        <ModeSelector
          mode={mode}
          onModeChange={onModeChange}
          actDisabled={actDisabled}
        />
        <ModelSelector
          services={llmServices}
          activeName={activeLlm?.name ?? null}
          onActivate={activateLlm}
          isActivating={isActivating}
        />
      </FooterControls>
    </FooterCard>
  );
}

const AiAgentPageContent: React.FC = () => {
  const {
    messages,
    messagesLoaded,
    currentConversationId,
    conversations,
    isLoadingConversations,
    inputValue,
    setInputValue,
    isLoading,
    newConversation,
    selectConversation,
    deleteConversation,
    renameConversation,
    sendUserMessageStream,
    stopGeneration,
  } = useAiChatContext();

  const { mode, setMode, actDisabled } = useActModeGate();
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null);
  const [pendingRenameId, setPendingRenameId] = useState<string | null>(null);
  // The split is remembered per user under the group id (TTTL-186), the
  // same way the Studio workbench remembers its panels.
  const layout = {
    id: LAYOUT_ID,
    ...useDefaultLayout({ id: LAYOUT_ID, storage: userStorage }),
  };
  const modalOpen = useAnyModalOpen();
  const titleOf = (id: string | null) =>
    conversations.find((c) => c.id === id)?.title || undefined;

  const handleSubmit = useCallback(() => {
    sendUserMessageStream([], AGENT_SYSTEM_PROMPT, {
      allowWrites: true,
      mode,
    });
  }, [sendUserMessageStream, mode]);

  const {
    listed: listedConversations,
    newActive,
    state: listState,
  } = sessionList({
    conversations,
    currentId: currentConversationId,
    messagesLoaded,
    messageCount: messages.length,
    loading: isLoadingConversations,
  });
  // The chat body waits with the list (its first chat is picked from it)
  // and again whenever a saved chat's history is being fetched.
  const chatLoading =
    isLoadingConversations ||
    (currentConversationId != null && !messagesLoaded);

  return (
    <Container data-testid="ai-agent-page">
      <OuterCard>
        <PageGroup orientation="horizontal" disabled={modalOpen} {...layout}>
          {/* biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout ids — deliberately stable, they key the remembered split */}
          <Panel
            id="agent-sessions"
            defaultSize={SIDEBAR.initial}
            minSize={SIDEBAR.min}
            maxSize={SIDEBAR.max}
            groupResizeBehavior="preserve-pixel-size"
          >
            <SessionSidebar
              conversations={listedConversations}
              currentId={currentConversationId ?? undefined}
              onSelect={selectConversation}
              onNew={newConversation}
              newActive={newActive}
              onRename={setPendingRenameId}
              onDelete={setPendingDeleteId}
              loading={listState === "loading"}
            />
          </Panel>
          <SessionsSeparator />
          {/* biome-ignore lint/correctness/useUniqueElementIds: react-resizable-panels layout id — deliberately stable */}
          <Panel id="agent-chat" minSize="320px">
            <ChatArea>
              <ChatWindow
                messages={messages}
                isThinking={isLoading}
                loading={chatLoading}
                layout="page"
              />
              <AgentFooter
                inputValue={inputValue}
                onInputChange={setInputValue}
                onSubmit={handleSubmit}
                onAbort={stopGeneration}
                isLoading={isLoading}
                mode={mode}
                onModeChange={setMode}
                actDisabled={actDisabled}
              />
            </ChatArea>
          </Panel>
        </PageGroup>
      </OuterCard>
      <ChatNameModal
        isOpen={pendingRenameId !== null}
        mode="rename"
        defaultName={titleOf(pendingRenameId)}
        onClose={() => setPendingRenameId(null)}
        onSave={(name) => {
          if (pendingRenameId) renameConversation(pendingRenameId, name);
        }}
      />
      <ChatDeleteModal
        isOpen={pendingDeleteId !== null}
        chatTitle={titleOf(pendingDeleteId)}
        onClose={() => setPendingDeleteId(null)}
        onConfirm={() => {
          if (!pendingDeleteId) return;
          const wasCurrent = pendingDeleteId === currentConversationId;
          void deleteConversation(pendingDeleteId);
          // Deleting the open chat lands on New, not on whichever chat
          // happens to be next in the list. Same tick as the removal, so
          // the two selection changes render once.
          if (wasCurrent) newConversation();
        }}
      />
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  height: 100%;
  padding: 20px;
  background: ${(p) => p.theme.semanticAppBackground};
`;

const OuterCard = styled.div`
  display: flex;
  flex: 1;
  min-width: 0;
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-radius: ${tk.ui.cards.corner}px;
  background: ${(p) => p.theme.cards1Background};
  overflow: hidden;
`;

const PageGroup = styled(Group)`
  width: 100%;
  height: 100%;
`;

// Bridge_Sidebar draws its right edge in card chrome, not Studio's lighter
// panel hairline.
const SessionsSeparator = styled(ColumnSeparator).attrs<{ $line?: string }>(
  (p) => ({ $line: p.theme.cards1Border }),
)``;

const Sidebar = styled.aside`
  height: 100%;
  display: flex;
  flex-direction: column;
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  height: ${tk.ui.sidebar.header.height}px;
  padding: 0 ${tk.ui.semantic.spacingm}px;
  flex-shrink: 0;
`;

const SidebarTitle = styled.span`
  font-size: ${SINGLE_LINE.small.uppercase.fontSize}px;
  font-weight: ${SINGLE_LINE.small.uppercase.fontWeight};
  line-height: 1.2;
  text-transform: uppercase;
  color: ${(p) => p.theme.semanticTextLight};
`;

const SessionList = styled.div`
  flex: 1;
  /* A flex child's default minimum is its content, which would push a
     long list past the sidebar instead of scrolling it (TTTL-186). */
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const SkeletonRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${LIST_ITEM.gap}px;
  width: 100%;
  height: ${LIST_ITEM.height}px;
  flex-shrink: 0;
  padding: 0 ${LIST_ITEM.paddingw}px;
  box-sizing: border-box;
`;

const Row = styled.div<{ $active: boolean }>`
  display: flex;
  align-items: center;
  gap: ${LIST_ITEM.gap}px;
  width: 100%;
  height: ${LIST_ITEM.height}px;
  flex-shrink: 0;
  padding: 0 ${LIST_ITEM.paddingw}px;
  box-sizing: border-box;
  font-size: ${SINGLE_LINE.default.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.default.medium.fontWeight};
  line-height: 1.2;
  color: ${(p) => p.theme.semanticTextBody};
  background: ${(p) =>
    p.$active ? p.theme.listItemActiveBackground : "transparent"};

  &:hover {
    background: ${(p) =>
      p.$active
        ? p.theme.listItemActiveBackground
        : p.theme.listItemBackgroundHover};
  }
`;

const RowIcon = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${LIST_ITEM.wrap}px;
  height: ${LIST_ITEM.wrap}px;
`;

// The session itself: fills the row up to the trailing control and
// inherits its type and colour. The library's reset leaves buttons bare.
const RowButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${LIST_ITEM.gap}px;
  flex: 1;
  min-width: 0;
  height: 100%;
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
`;

const RowLabel = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// The dropdown's root box would stretch across the row and squeeze the
// label out; the slot keeps it at the button's size.
const MenuSlot = styled.span`
  display: inline-flex;
  flex-shrink: 0;

  & > * {
    width: auto;
  }
`;

// The row's trailing menu button, shown on hover and keyboard focus (and
// held while its menu is open) as the Figma List/item describes it.
const RowTrailing = styled.span<{ $open: boolean }>`
  display: inline-flex;
  flex-shrink: 0;
  opacity: ${(p) => (p.$open ? 1 : 0)};

  ${Row}:hover &,
  ${Row}:focus-within & {
    opacity: 1;
  }
`;

const ChatArea = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 0;
`;

const FooterCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${tk.ui.semantic.spacingl}px;
  /* The content column: 800px at most, and on a narrow panel the width
     left by the chat body's side padding, so the two stay aligned. */
  width: calc(100% - 2 * ${tk.ui.semantic.spacingl}px);
  max-width: ${PAGE_CONTENT_WIDTH}px;
  flex-shrink: 0;
  padding: ${tk.ui.semantic.spacingm}px;
  box-sizing: border-box;
  background: ${(p) => p.theme.cards1Background};
  border: 1px solid ${(p) => p.theme.cards1Border};
  border-bottom: none;
  border-radius: ${tk.ui.cards.corner}px ${tk.ui.cards.corner}px 0 0;
`;

const FooterControls = styled.div`
  display: flex;
  align-items: center;
  gap: ${tk.ui.semantic.spacings}px;

  /* The dropdowns' root boxes stretch to the row; the buttons size to
     their text instead. */
  & > * {
    width: auto;
  }
`;

export const AiAgentPage: React.FC = () => (
  <AiChatProvider conversationContext="agent">
    <AiAgentPageContent />
  </AiChatProvider>
);

export default AiAgentPage;
